import { Link } from '@heroui/react';
import { ComponentProps, forwardRef, Ref } from 'react';
import { useLocale } from '~/hooks/useLocale';

type BaseLinkProps = Omit<ComponentProps<typeof Link>, 'href' | 'routerOptions'> & {
  href: string;
  variant?: 'primary' | 'secondary';
  routerOptions?: {
    locale?: string;
    shallow?: boolean;
    [key: string]: any;
  };
};

export const formatHref = (localeCode: string, href: string, removeLeadingSlash = false) => {
  const formattedHref = href.startsWith('http') || href.match(/^\/\w{2}-\w{2}\//)
    ? href
    : `/${localeCode}${href.startsWith('/') ? href : `/${href}`}`;

  return removeLeadingSlash ? formattedHref.substring(1) : formattedHref;
};

export const formatInternalHref = (localeCode: string, href: string, removeLeadingSlash = false) => {
  let formattedHref = href;

  if (href.startsWith('http')) {
    // Replace domain.com/path with domain.com/locale/path
    formattedHref = href.replace(
      /^(https?:\/\/[^/]+)(\/[^/].*)/,
      `$1/${localeCode}$2`
    );
  } else {
    formattedHref = `/${localeCode}${href.startsWith('/') ? href : `/${href}`}`;
  }

  return removeLeadingSlash ? formattedHref.substring(1) : formattedHref;
};

export const BaseLink = forwardRef(({
  href,
  variant = 'primary',
  routerOptions,
  ...props
}: BaseLinkProps, ref: Ref<HTMLAnchorElement>) => {
  const { currentLocale } = useLocale();

  // Prepare href with locale
  const formattedHref = formatHref(currentLocale.code, href);

  // Create merged router options
  const mergedRouterOptions = routerOptions ? {
    locale: currentLocale.code,
    shallow: false,
    ...routerOptions
  } : undefined;

  // Setup link props
  const linkProps: any = {
    variant,
    href: formattedHref,
    ref,
    ...props
  };

  // Only add routerOptions if defined to avoid type errors
  if (mergedRouterOptions) {
    linkProps.routerOptions = mergedRouterOptions;
  }

  return <Link {...linkProps} />;
});

BaseLink.displayName = 'BaseLink'; 
